import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Search, TrendingUp, TrendingDown, BarChart3, AlertCircle, CheckCircle, ArrowLeft, Wallet, Shield } from 'lucide-react';
import api from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';
import StockChart from '../components/stocks/StockChart';
import TechnicalIndicatorsChart from '../components/stocks/TechnicalIndicatorsChart';
import OwnershipInsiderTradingSection from '../components/stocks/OwnershipInsiderTradingSection';
import MarketOverviewSection from '../components/stocks/MarketOverviewSection';
import StockSentimentSection from '../components/stocks/StockSentimentSection';
import PositionScalingSection from '../components/stocks/PositionScalingSection';
import StructuredData from '../components/StructuredData';
import CanonicalURL from '../components/CanonicalURL';
import Disclaimer from '../components/Disclaimer';

const IndividualStockAnalysis = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDemo = user?.isDemo === true;
  const [symbol, setSymbol] = useState('');
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState(null);
  const [years, setYears] = useState(5); // Premium: Up to 5 years of data
  const [timeframe, setTimeframe] = useState('day'); // Premium: Multiple timeframes
  const [marketOverviewRefresh, setMarketOverviewRefresh] = useState(0); // Trigger for Market Overview refresh
  const [investAmount, setInvestAmount] = useState(null); // From Dashboard "Ready to Invest" flow
  const [riskProfile, setRiskProfile] = useState('moderate'); // conservative | moderate | aggressive
  
  // Load URL params and user settings on mount
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const amount = queryParams.get('investAmount');
    const risk = queryParams.get('risk');
    if (amount) setInvestAmount(parseFloat(amount) || null);
    if (risk && ['conservative', 'moderate', 'aggressive'].includes(risk)) setRiskProfile(risk);
  }, [location.search]);

  useEffect(() => {
    const loadRiskProfile = async () => {
      try {
        const res = await api.get('/api/users/settings');
        const profile = res.data?.settings?.risk_profile;
        if (profile && ['conservative', 'moderate', 'aggressive'].includes(profile)) {
          setRiskProfile(profile);
        }
      } catch (_) { /* ignore */ }
    };
    loadRiskProfile();
  }, []);

  const saveRiskProfile = async (profile) => {
    setRiskProfile(profile);
    try {
      await api.put('/api/users/settings/risk-profile', { risk_profile: profile });
    } catch (_) { /* ignore */ }
  };
  
  // Load symbol from URL params on mount and auto-analyze
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const symbolFromUrl = queryParams.get('symbol');
    if (symbolFromUrl) {
      const symbolUpper = symbolFromUrl.toUpperCase();
      setSymbol(symbolUpper);
      // Trigger analysis if symbol is from URL
      const timer = setTimeout(() => {
        handleAnalyze({ preventDefault: () => {} }, symbolUpper);
      }, 100);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleAnalyze = async (e, symbolOverride = null) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    const currentSymbol = (symbolOverride || symbol).trim().toUpperCase();
    if (!currentSymbol) return;

    setLoading(true);
    setError(null);
    setAnalysis(null);

    try {
      // Premium plan: Include years parameter for extended historical data
      const response = await api.post('/api/stocks/analyze', { 
        symbol: currentSymbol,
        years: years, // Premium: Request up to 5 years of data
        timeframe: timeframe
      });
      if (response.data.success) {
        setAnalysis(response.data.data);
        // Trigger Market Overview refresh after analysis completes
        setMarketOverviewRefresh(prev => prev + 1);
      } else {
        setError(response.data.message || 'Analysis failed');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to analyze stock. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <StructuredData type="application" page="individual-stock" symbol={symbol || analysis?.symbol} />
      <CanonicalURL />
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Back to Bulk Analysis Button */}
        {location.state?.fromBulkAnalysis && (
          <button
            onClick={() => {
              sessionStorage.removeItem('bulkAnalysisResults');
              sessionStorage.removeItem('bulkAnalysisStats');
              navigate(-1); // Go back to the previous page (BulkStockAnalysis)
            }}
            className="mb-6 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <ArrowLeft className="-ml-1 mr-2 h-5 w-5 text-gray-500" aria-hidden="true" />
            Back to Full Analysis
          </button>
        )}
        
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Individual Stock Analysis</h1>
          <p className="text-gray-600">Get comprehensive analysis for any stock symbol</p>
          <Disclaimer variant="investment" />
          {isDemo && (
            <p className="mt-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 inline-block">
              Demo: Limited to 2 stock analyses per session. <Link to="/register" className="underline font-medium">Sign up</Link> for unlimited access.
            </p>
          )}
        </div>

        {/* Invest Amount Banner - from Dashboard "Ready to Invest" flow */}
        {investAmount != null && investAmount > 0 && (
          <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center">
            <Wallet className="h-8 w-8 text-emerald-600 mr-3 flex-shrink-0" />
            <div>
              <p className="font-semibold text-emerald-900">
                You're considering investing {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(investAmount)}.
              </p>
              <p className="text-sm text-emerald-700 mt-0.5">
                Here are stocks that match your <span className="font-medium capitalize">{riskProfile}</span> risk profile. Use the risk selector below to adjust.
              </p>
            </div>
          </div>
        )}

        {/* Search Form */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <form onSubmit={handleAnalyze} className="space-y-4">
            <div className="flex gap-4">
              <div className="flex-1">
                <label htmlFor="symbol" className="block text-sm font-medium text-gray-700 mb-2">
                  Stock Symbol
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    id="symbol"
                    type="text"
                    value={symbol}
                    onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                    placeholder="Enter stock symbol (e.g., AAPL, MSFT, TSLA)"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    maxLength={10}
                  />
                </div>
              </div>
              <div className="flex items-end">
                <button
                  type="submit"
                  disabled={loading || !symbol.trim()}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
                >
                  {loading ? 'Analyzing...' : 'Analyze'}
                </button>
              </div>
            </div>
            
            {/* Premium Plan Options */}
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <label htmlFor="years" className="block text-sm font-medium text-gray-700 mb-2">
                  Historical Data Range (Premium)
                </label>
                <select
                  id="years"
                  value={years}
                  onChange={(e) => setYears(parseInt(e.target.value))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={1}>1 Year</option>
                  <option value={2}>2 Years</option>
                  <option value={3}>3 Years</option>
                  <option value={5}>5 Years (Max)</option>
                </select>
              </div>
              <div className="flex-1">
                <label htmlFor="timeframe" className="block text-sm font-medium text-gray-700 mb-2">
                  Timeframe
                </label>
                <select
                  id="timeframe"
                  value={timeframe}
                  onChange={(e) => setTimeframe(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="day">Daily</option>
                  <option value="week">Weekly</option>
                  <option value="month">Monthly</option>
                </select>
              </div>
              <div className="flex-1">
                <label htmlFor="riskProfile" className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                  <Shield className="h-4 w-4 mr-1" />
                  Risk Profile
                </label>
                <select
                  id="riskProfile"
                  value={riskProfile}
                  onChange={(e) => saveRiskProfile(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="conservative">Conservative</option>
                  <option value="moderate">Moderate</option>
                  <option value="aggressive">Aggressive</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {riskProfile === 'conservative' && 'Lower volatility, dividend focus'}
                  {riskProfile === 'moderate' && 'Balanced growth and stability'}
                  {riskProfile === 'aggressive' && 'Higher growth, higher volatility'}
                </p>
              </div>
              <div className="text-xs text-gray-500 flex items-end pb-2">
                <span>🚀 Premium Plan Active</span>
              </div>
            </div>
          </form>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start">
            <AlertCircle className="h-5 w-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Market Overview Section - Always visible */}
        <MarketOverviewSection refreshTrigger={marketOverviewRefresh} />

        {/* Analysis Results */}
        {analysis && (
          <div className="space-y-6">
            {/* Interactive Stock Chart */}
            {analysis.fullCandles && analysis.fullCandles.length > 0 && (
              <StockChart
                candleData={analysis.fullCandles}
                analysis={analysis}
                symbol={analysis.symbol}
              />
            )}

            {/* Stock Sentiment Section */}
            <StockSentimentSection symbol={analysis.symbol} />

            {/* Stock Overview */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{analysis.symbol}</h2>
                  {analysis.companyName && (
                    <p className="text-sm text-gray-600 mt-1">{analysis.companyName}</p>
                  )}
                </div>
                {analysis.signal === 'BUY' ? (
                  <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium flex items-center">
                    <CheckCircle className="h-4 w-4 mr-1" />
                    BUY Signal
                  </span>
                ) : analysis.signal === 'SELL' ? (
                  <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    SELL Signal
                  </span>
                ) : (
                  <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                    HOLD Signal
                  </span>
                )}
              </div>

              {analysis.currentPrice && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <div>
                    <p className="text-sm text-gray-600">Current Price</p>
                    <p className="text-2xl font-bold text-gray-900">${analysis.currentPrice.toFixed(2)}</p>
                  </div>
                  {analysis.priceChange && (
                    <div>
                      <p className="text-sm text-gray-600">Change</p>
                      <p className={`text-2xl font-bold flex items-center ${
                        analysis.priceChange >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {analysis.priceChange >= 0 ? <TrendingUp className="h-5 w-5 mr-1" /> : <TrendingDown className="h-5 w-5 mr-1" />}
                        {analysis.priceChange >= 0 ? '+' : ''}{analysis.priceChange.toFixed(2)}%
                      </p>
                    </div>
                  )}
                  {analysis.confidence && (
                    <div>
                      <p className="text-sm text-gray-600">Confidence</p>
                      <p className="text-2xl font-bold text-gray-900">{analysis.confidence}%</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Technical Indicators - Chart View */}
            {analysis.indicators && analysis.fullCandles && (
              <TechnicalIndicatorsChart
                candleData={analysis.fullCandles}
                analysis={analysis}
              />
            )}

            {/* Technical Indicators - Summary Cards */}
            {analysis.indicators && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <BarChart3 className="h-5 w-5 mr-2" />
                  Technical Indicators Summary
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {analysis.indicators.rsi && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">RSI (14)</p>
                      <p className="text-xl font-bold text-gray-900">{analysis.indicators.rsi.toFixed(2)}</p>
                      <p className={`text-xs mt-1 ${
                        analysis.indicators.rsi < 30 ? 'text-green-600' :
                        analysis.indicators.rsi > 70 ? 'text-red-600' : 'text-yellow-600'
                      }`}>
                        {analysis.indicators.rsi < 30 ? 'Oversold' :
                         analysis.indicators.rsi > 70 ? 'Overbought' : 'Neutral'}
                      </p>
                    </div>
                  )}
                  {analysis.indicators.macd && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">MACD</p>
                      <p className="text-xl font-bold text-gray-900">{analysis.indicators.macd.toFixed(2)}</p>
                      <p className={`text-xs mt-1 ${analysis.indicators.macd > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {analysis.indicators.macd > 0 ? 'Bullish' : 'Bearish'}
                      </p>
                    </div>
                  )}
                  {analysis.indicators.volume && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">Volume</p>
                      <p className="text-xl font-bold text-gray-900">{analysis.indicators.volume.toLocaleString()}</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {analysis.positionScaling && (
              <PositionScalingSection framework={analysis.positionScaling} />
            )}

            {/* Analysis Details */}
            {analysis.analysis && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Analysis Details</h3>
                <div className="prose max-w-none">
                  <p className="text-gray-700 whitespace-pre-wrap">{analysis.analysis}</p>
                </div>
              </div>
            )}

            {/* Risk Assessment */}
            {analysis.riskScore !== undefined && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Risk Assessment</h3>
                <div className="flex items-center">
                  <div className="flex-1 bg-gray-200 rounded-full h-4 mr-4">
                    <div
                      className={`h-4 rounded-full ${
                        analysis.riskScore <= 3 ? 'bg-green-500' :
                        analysis.riskScore <= 6 ? 'bg-yellow-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${(analysis.riskScore / 10) * 100}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-700">
                    Risk Score: {analysis.riskScore}/10
                  </span>
                </div>
              </div>
            )}

            {/* Ownership & Insider Trading Section */}
            <OwnershipInsiderTradingSection symbol={analysis.symbol} />
          </div>
        )}

        {/* Empty State */}
        {!analysis && !loading && !error && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Analysis Yet</h3>
            <p className="text-gray-600">Enter a stock symbol above to get started</p>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default IndividualStockAnalysis;

